import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from 'common/components/Button';
import { AlertTriangle, Lock, Ban, Search, Wrench, Globe, Clock, Timer } from 'lucide-react';
import styles from './ErrorPage.module.scss';

interface ErrorInfo {
    status: number;
    title: string;
    message: string;
    icon: React.ComponentType<{ size?: number; className?: string }>;
}

const errorTypes: Record<number, ErrorInfo> = {
    400: {
        status: 400,
        title: 'Bad Request',
        message: 'The request was invalid or cannot be served. Please check your input and try again.',
        icon: AlertTriangle,
    },
    401: {
        status: 401,
        title: 'Unauthorized',
        message: 'You need to be logged in to access this resource. Please log in to continue.',
        icon: Lock,
    },
    403: {
        status: 403,
        title: 'Forbidden',
        message:
            'You do not have permission to access this resource. Contact your administrator if you believe this is an error.',
        icon: Ban,
    },
    404: {
        status: 404,
        title: 'Page Not Found',
        message: 'The page you are looking for does not exist. Please check the URL or go back to the homepage.',
        icon: Search,
    },
    500: {
        status: 500,
        title: 'Internal Server Error',
        message: 'Something went wrong on our end. We are working to fix this issue. Please try again later.',
        icon: Wrench,
    },
    502: {
        status: 502,
        title: 'Bad Gateway',
        message:
            'The server received an invalid response. This is usually temporary. Please try again in a few moments.',
        icon: Globe,
    },
    503: {
        status: 503,
        title: 'Service Unavailable',
        message: 'The service is temporarily unavailable. We are performing maintenance. Please try again later.',
        icon: Clock,
    },
    504: {
        status: 504,
        title: 'Gateway Timeout',
        message: 'The server did not respond in time. Please try again. If the problem persists, contact support.',
        icon: Timer,
    },
};

export const ErrorPage: React.FC = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const errorCode = parseInt(searchParams.get('code') || '404');
    const [errorDetails, setErrorDetails] = useState<any>(null);

    const errorInfo = errorTypes[errorCode] || errorTypes[404];

    // Load error details from sessionStorage on component mount
    useEffect(() => {
        const storedError = sessionStorage.getItem('lastError');
        if (storedError) {
            try {
                const errorData = JSON.parse(storedError);
                setErrorDetails(errorData);
                console.log('🔍 Previous error details:', errorData);

                // Clear the stored error after displaying
                sessionStorage.removeItem('lastError');
            } catch (err) {
                console.error('Failed to parse stored error:', err);
            }
        }
    }, []);

    const handleGoHome = () => {
        window.location.href = '/';
    };

    const handleGoBack = () => {
        window.history.back();
    };

    return (
        <div className={styles.errorPage}>
            <div className={styles.errorContainer}>
                <div className={styles.errorIcon}>
                    <errorInfo.icon size={64} />
                </div>

                <div className={styles.errorCode}>{errorInfo.status}</div>

                <h1 className={styles.errorTitle}>{errorInfo.title}</h1>

                <p className={styles.errorMessage}>{errorInfo.message}</p>

                <div className={styles.errorActions}>
                    <Button variant='primary' onClick={handleGoHome}>
                        Go Home
                    </Button>

                    <Button variant='secondary' onClick={handleGoBack}>
                        Go Back
                    </Button>
                </div>

                <div className={styles.errorDetails}>
                    <p className={styles.errorTimestamp}>Error occurred at: {new Date().toLocaleString()}</p>

                    {/* Debug information for developers */}
                    {errorDetails && (
                        <details style={{ marginTop: '1rem', textAlign: 'left' }}>
                            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                                🔍 Debug Information (Click to expand)
                            </summary>
                            <div
                                style={{
                                    background: '#f5f5f5',
                                    padding: '1rem',
                                    borderRadius: '4px',
                                    fontSize: '0.875rem',
                                    fontFamily: 'monospace',
                                    whiteSpace: 'pre-wrap',
                                    overflow: 'auto',
                                    maxHeight: '200px',
                                }}
                            >
                                {JSON.stringify(errorDetails, null, 2)}
                            </div>
                        </details>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ErrorPage;
